import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowRight, Building2, TrendingUp, PlusCircle, MapPin, Users, Phone } from 'lucide-react';
import axios from 'axios';
import NewsletterSignup from '../components/NewsletterSignup';
import { getListingDetailPath } from '../lib/slugUtils';
import { PageSEO } from '../components/SEO';

/**
 * HomeTasteSkillV3Final - Finalisierte Version
 * 
 * Optimierungen gegenüber V3:
 * 1. Hero: Kleinere Überschrift, optimierte Zeilenumbrüche, Trust-Elemente in einer Zeile
 * 2. Marktplatz-Vorschau: Größere Namen, hervorgehobene Preise, lesbarere Metadaten
 * 3. Service-Karten: Identische Höhe, Button-Alignment, harmonisierte Abstände
 */

const HomeTasteSkillV3Final = () => {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadListings = async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/listings`);
        const data = Array.isArray(res.data) ? res.data : [];
        const available = data.filter(l => l.status === 'Verfügbar').slice(0, 3);
        setListings(available);
      } catch (error) {
        console.error('Failed to load listings:', error);
      } finally {
        setLoading(false);
      }
    };
    loadListings();
  }, []);

  return (
    <>
      <PageSEO 
        title="Firmbay – GmbH kaufen, verkaufen, gründen"
        description="Firmbay – Vermittlung von GmbH-Gründungen und Bestandsgesellschaften. Digitaler Marktplatz und persönliche Begleitung für GmbH-Transaktionen in Deutschland. Ein Angebot der Interpohl Solutions GmbH."
        path="/"
      />
      <div className="min-h-screen bg-white">
      {/* Hero Section - Optimierte Proportionen */}
      <section className="pt-20 pb-12 lg:pt-24 lg:pb-14 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-[#F8FAFA] to-white">
        <div className="max-w-[1280px] mx-auto">
          <div className="grid lg:grid-cols-12 gap-6 lg:gap-8 items-start">
            
            {/* Left: Content - 5 Spalten für bessere Balance */}
            <div className="lg:col-span-5">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#E9F7F6] rounded-full mb-4">
                <div className="w-1.5 h-1.5 bg-[#009E9A] rounded-full"></div>
                <span className="text-[11px] font-semibold text-[#009E9A] uppercase tracking-wider">
                  Unternehmensvermittlung
                </span>
              </div>
              
              {/* Kleinere Überschrift mit optimierten Zeilenumbrüchen */}
              <h1 className="text-[28px] md:text-[32px] lg:text-[36px] font-bold text-[#0B1930] mb-3 leading-[1.15] tracking-[-0.02em]">
                GmbH kaufen. Unternehmen<br className="hidden lg:block" /> verkaufen. Zukunft gestalten.
              </h1>
              
              <p className="text-base text-gray-600 mb-5 leading-relaxed max-w-md">
                Digitaler Marktplatz und persönliche Begleitung für GmbH-Transaktionen in Deutschland.
              </p>

              {/* Zwei gleichwertige CTAs */}
              <div className="flex flex-col sm:flex-row gap-3 mb-5">
                <Link to="/marktplatz">
                  <Button className="w-full sm:w-auto bg-[#0B1930] hover:bg-[#162844] text-white text-sm font-semibold px-5 py-2.5 rounded-lg transition-colors">
                    GmbH-Angebote entdecken
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
                <Link to="/kontakt">
                  <Button variant="outline" className="w-full sm:w-auto border-[#0B1930] text-[#0B1930] hover:bg-[#E9F7F6] text-sm font-semibold px-5 py-2.5 rounded-lg transition-colors">
                    <Phone className="mr-2 w-4 h-4" />
                    Persönliche Beratung
                  </Button>
                </Link>
              </div>

              {/* Trust-Elemente in einer Zeile auf Desktop */}
              <div className="flex items-center gap-3 text-sm text-gray-500 flex-wrap lg:flex-nowrap">
                <span className="whitespace-nowrap">Diskrete Abwicklung</span>
                <span className="text-gray-300 hidden sm:inline">|</span>
                <span className="whitespace-nowrap">Persönliche Betreuung</span>
                <span className="text-gray-300 hidden sm:inline">|</span>
                <span className="whitespace-nowrap">Strukturierte Prozesse</span>
              </div>
            </div>

            {/* Right: Marketplace Preview - 7 Spalten */}
            <div className="lg:col-span-7">
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                {/* Header */}
                <div className="px-4 py-2.5 border-b border-gray-100 bg-[#F8FAFA]">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-[#0B1930] uppercase tracking-wider">Aktuelle GmbH-Angebote</span>
                    <span className="text-xs text-gray-400">{loading ? '...' : `${listings.length} verfügbar`}</span>
                  </div>
                </div>
                
                {/* Listings - Optimierte Darstellung */}
                <div className="divide-y divide-gray-100">
                  {loading ? (
                    <div className="px-4 py-6 text-center text-sm text-gray-400">
                      Angebote werden geladen...
                    </div>
                  ) : listings.length > 0 ? (
                    listings.map((listing, idx) => (
                      <Link 
                        key={listing.id || idx} 
                        to={getListingDetailPath(listing)}
                        className="block px-4 py-3.5 hover:bg-[#F8FAFA]/50 transition-colors group"
                        data-testid={`home-listing-${listing.id}`}
                      >
                        <div className="flex items-center justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="inline-flex px-2 py-0.5 text-[10px] font-semibold rounded bg-[#E9F7F6] text-[#009E9A]">
                                Verfügbar
                              </span>
                              <span className="text-[11px] text-gray-500 font-medium">{listing.industry}</span>
                            </div>
                            {/* Größerer Gesellschaftsname */}
                            <div className="text-[15px] font-bold text-[#0B1930] mb-1 truncate group-hover:text-[#009E9A] transition-colors">
                              {listing.title}
                            </div>
                            {/* Lesbarere Metadaten */}
                            <div className="flex items-center gap-4 text-[13px] text-gray-600">
                              <span className="flex items-center gap-1">
                                <MapPin className="w-3.5 h-3.5 text-gray-400" />
                                {listing.location}
                              </span>
                              <span>Gründung {listing.founded_year}</span>
                            </div>
                          </div>
                          {/* Hervorgehobener Kaufpreis */}
                          <div className="text-right flex-shrink-0">
                            <div className="text-[10px] text-gray-400 uppercase tracking-wide mb-0.5">Kaufpreis</div>
                            <div className="text-base font-bold text-[#0B1930]">
                              {listing.price?.toLocaleString('de-DE')} €
                            </div>
                          </div>
                        </div>
                      </Link>
                    ))
                  ) : (
                    <div className="px-4 py-6 text-center text-sm text-gray-400">
                      Derzeit keine Angebote verfügbar
                    </div>
                  )}
                </div>
                
                {/* Footer */}
                <div className="px-4 py-2.5 border-t border-gray-100 bg-[#F8FAFA]">
                  <Link to="/marktplatz" className="flex items-center justify-center gap-2 text-sm font-medium text-[#009E9A] hover:text-[#008580] transition-colors">
                    Alle Angebote ansehen
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section - Identische Höhe und Button-Alignment */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-white border-t border-gray-100">
        <div className="max-w-[1280px] mx-auto">
          <div className="max-w-xl mb-8">
            <h2 className="text-2xl md:text-[26px] font-bold text-[#0B1930] mb-2 tracking-[-0.01em]">
              Unsere Leistungen
            </h2>
            <p className="text-gray-600 text-[15px]">
              Ob Kauf, Verkauf oder Neugründung – wir begleiten Sie bei jedem Schritt.
            </p>
          </div>

          {/* 3 gleichwertige Service-Karten mit flex für identische Höhe */}
          <div className="grid md:grid-cols-3 gap-5">
            {[
              {
                icon: Building2,
                title: 'GmbH kaufen',
                description: 'Übernehmen Sie eine bestehende Gesellschaft mit Handelsregistereintragung und starten Sie schneller.',
                link: '/marktplatz',
                button: 'Zum Marktplatz'
              },
              {
                icon: TrendingUp,
                title: 'GmbH verkaufen',
                description: 'Diskrete Vermittlung Ihrer Gesellschaft an geprüfte und qualifizierte Interessenten.',
                link: '/seller-onboarding',
                button: 'Jetzt anbieten'
              },
              {
                icon: PlusCircle,
                title: 'GmbH gründen',
                description: 'Strukturierte Begleitung von der Vorbereitung bis zur Handelsregistereintragung.',
                link: '/onboarding',
                button: 'Formular starten'
              }
            ].map((service) => {
              const Icon = service.icon;
              return (
                <div 
                  key={service.title} 
                  className="group bg-white border border-gray-200 rounded-xl p-5 hover:border-[#009E9A] hover:shadow-md transition-all flex flex-col h-full"
                >
                  <div className="w-10 h-10 bg-[#E9F7F6] rounded-lg flex items-center justify-center mb-4 group-hover:bg-[#009E9A] transition-colors flex-shrink-0">
                    <Icon className="w-5 h-5 text-[#009E9A] group-hover:text-white transition-colors" />
                  </div>
                  <h3 className="text-lg font-bold text-[#0B1930] mb-2">{service.title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed flex-grow mb-4">
                    {service.description}
                  </p>
                  {/* Button am unteren Rand fixiert */}
                  <Link to={service.link} className="mt-auto">
                    <Button className="w-full justify-center bg-[#0B1930] text-white hover:bg-[#162844] text-sm rounded-lg">
                      {service.button}
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </Link>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Persönliche Unternehmensvermittlung - Kompakter */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-[#F8FAFA]">
        <div className="max-w-[1280px] mx-auto">
          <div className="grid lg:grid-cols-2 gap-8 items-start">
            {/* Text */}
            <div>
              <div className="inline-flex items-center gap-2 mb-3">
                <Users className="w-5 h-5 text-[#009E9A]" />
                <span className="text-sm font-semibold text-[#009E9A]">Persönliche Vermittlung</span>
              </div>
              <h2 className="text-2xl md:text-[26px] font-bold text-[#0B1930] mb-3 tracking-[-0.01em]">
                Individuelle Begleitung bei jeder Transaktion
              </h2>
              <p className="text-gray-600 leading-relaxed mb-5 text-[15px]">
                Neben dem digitalen Marktplatz bieten wir persönliche Beratung und Vermittlung. 
                Wir analysieren Ihre Situation, identifizieren passende Gesellschaften oder Käufer 
                und begleiten Sie strukturiert durch den gesamten Prozess.
              </p>
              
              <div className="space-y-2.5 mb-5">
                {[
                  'Vertrauliche Behandlung aller Unterlagen',
                  'Persönlicher Ansprechpartner während des gesamten Prozesses',
                  'Koordination mit Notaren, Steuerberatern und weiteren Beteiligten'
                ].map((item, idx) => (
                  <div key={idx} className="flex items-start gap-2.5">
                    <div className="w-5 h-5 rounded-full bg-[#009E9A] flex items-center justify-center flex-shrink-0 mt-0.5">
                      <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <span className="text-sm text-gray-700">{item}</span>
                  </div>
                ))}
              </div>

              <Link to="/kontakt">
                <Button className="bg-[#0B1930] hover:bg-[#162844] text-white text-sm font-semibold px-5 py-2.5 rounded-lg">
                  Beratungsgespräch vereinbaren
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>

            {/* Kontakt-Box - Kompakter */}
            <div className="bg-white rounded-xl border border-gray-200 p-5 lg:p-6">
              <h3 className="text-lg font-bold text-[#0B1930] mb-3">Direkter Kontakt</h3>
              <p className="text-sm text-gray-600 mb-5">
                Haben Sie Fragen oder möchten Sie ein unverbindliches Beratungsgespräch? 
                Wir sind persönlich für Sie da.
              </p>
              
              <div className="space-y-3 mb-5">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 bg-[#E9F7F6] rounded-lg flex items-center justify-center">
                    <Phone className="w-4 h-4 text-[#009E9A]" />
                  </div>
                  <div>
                    <div className="text-[11px] text-gray-500 uppercase tracking-wide">Telefon</div>
                    <div className="text-sm font-semibold text-[#0B1930]">0170-7967959</div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 bg-[#E9F7F6] rounded-lg flex items-center justify-center">
                    <svg className="w-4 h-4 text-[#009E9A]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div>
                    <div className="text-[11px] text-gray-500 uppercase tracking-wide">E-Mail</div>
                    <div className="text-sm font-semibold text-[#0B1930]">info@firmbay.de</div>
                  </div>
                </div>
              </div>

              <div className="pt-4 border-t border-gray-100">
                <Link to="/kontakt">
                  <Button variant="outline" className="w-full justify-center border-[#0B1930] text-[#0B1930] hover:bg-[#E9F7F6] text-sm font-semibold rounded-lg">
                    Kontaktformular öffnen
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Newsletter - Kompakter */}
      <section className="py-10 px-4 sm:px-6 lg:px-8 bg-[#E9F7F6]">
        <div className="max-w-[560px] mx-auto">
          <NewsletterSignup />
        </div>
      </section>
    </div>
    </>
  );
};

export default HomeTasteSkillV3Final;
